import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Briefcase, Loader2, Power, Archive, Trash2 } from "lucide-react";
import MissionCountdown from "@/components/MissionCountdown";

const STATUS = {
  planifiee: { label: "Planifiée", cls: "bg-blue-100 text-blue-700" },
  en_cours: { label: "En cours", cls: "bg-emerald-100 text-emerald-700" },
  terminee: { label: "Terminée", cls: "bg-slate-100 text-slate-700" },
  desactivee: { label: "Désactivée", cls: "bg-amber-100 text-amber-700" },
  archivee: { label: "Archivée", cls: "bg-slate-200 text-slate-500" },
};

const EMPTY = { libelle: "", convention_id: "", entreprise_id: "", salarie_id: "", date_debut: "", date_fin: "", lieu: "", description: "", statut: "planifiee" };

export default function Missions() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [entreprises, setEntreprises] = useState([]);
  const [salaries, setSalaries] = useState([]);
  const [conventions, setConventions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [filterStatus, setFilterStatus] = useState("all");
  const [showArchived, setShowArchived] = useState(false);
  const canManage = ["admin", "gestionnaire_ge"].includes(user?.role);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, b, c, d] = await Promise.all([
        api.get("/missions", { params: { include_archived: showArchived } }),
        user?.role === "salarie" ? Promise.resolve({ data: [] }) : api.get("/entreprises"),
        user?.role === "salarie" ? Promise.resolve({ data: [] }) : api.get("/salaries"),
        user?.role === "salarie" ? Promise.resolve({ data: [] }) : api.get("/conventions"),
      ]);
      setItems(a.data); setEntreprises(b.data); setSalaries(c.data); setConventions(d.data);
    } catch {} finally { setLoading(false); }
  }, [showArchived, user?.role]);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      if (!payload.convention_id) delete payload.convention_id;
      if (!payload.salarie_id) delete payload.salarie_id;
      await api.post("/missions", payload);
      toast.success("Mission créée"); setOpen(false); setForm(EMPTY); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const patchStatus = async (id, newStatus) => {
    try { await api.patch(`/missions/${id}`, { statut: newStatus }); toast.success(`Mission ${STATUS[newStatus]?.label.toLowerCase() || newStatus}`); load(); }
    catch { toast.error("Mise à jour impossible"); }
  };

  const archive = async (id) => {
    if (!window.confirm("Archiver cette mission ?")) return;
    try { await api.delete(`/missions/${id}`); toast.success("Mission archivée"); load(); }
    catch { toast.error("Archivage impossible"); }
  };

  const hardDelete = async (id) => {
    if (!window.confirm("Supprimer DÉFINITIVEMENT ? Irréversible.")) return;
    try { await api.delete(`/missions/${id}?hard=true`); toast.success("Supprimée"); load(); }
    catch { toast.error("Suppression impossible"); }
  };

  const entLabel = (id) => entreprises.find(e => e.id === id)?.raison_sociale || "—";
  const salLabel = (id) => { const s = salaries.find(x => x.id === id); return s ? `${s.prenom} ${s.nom}` : "—"; };
  const filtered = filterStatus === "all" ? items : items.filter((m) => m.statut === filterStatus);

  return (
    <div data-testid="missions-page">
      <PageHeader
        title={user?.role === "salarie" ? "Mes missions" : "Missions"}
        subtitle={user?.role === "salarie" ? "Vos missions en cours et à venir, synchronisées avec vos conventions." : "Planification, suivi et archivage des missions de mise à disposition."}
        actions={canManage ? (
          <div className="flex items-center gap-2 flex-wrap">
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="h-9 w-auto min-w-[140px]" data-testid="mis-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous statuts</SelectItem>
                {Object.entries(STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button size="sm" variant={showArchived ? "default" : "outline"} onClick={() => setShowArchived(!showArchived)} data-testid="mis-toggle-archived">
              {showArchived ? "Masquer archivées" : "Voir archivées"}
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="mission-add-btn" className="bg-[#C5A059] hover:bg-[#A88846] text-white">
                  <Plus className="h-4 w-4 mr-2" /> Nouvelle mission
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle>Nouvelle mission</DialogTitle></DialogHeader>
                <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="col-span-full"><Label>Libellé *</Label><Input required value={form.libelle} onChange={(e)=>setForm({...form, libelle: e.target.value})} data-testid="mis-libelle" /></div>
                  <div>
                    <Label>Entreprise *</Label>
                    <Select value={form.entreprise_id} onValueChange={(v)=>setForm({...form, entreprise_id: v})}>
                      <SelectTrigger data-testid="mis-entreprise"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                      <SelectContent>{entreprises.map(e=><SelectItem key={e.id} value={e.id}>{e.raison_sociale}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Salarié</Label>
                    <Select value={form.salarie_id} onValueChange={(v)=>setForm({...form, salarie_id: v})}>
                      <SelectTrigger><SelectValue placeholder="Optionnel" /></SelectTrigger>
                      <SelectContent>{salaries.map(s=><SelectItem key={s.id} value={s.id}>{s.prenom} {s.nom}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-full">
                    <Label>Convention liée</Label>
                    <Select value={form.convention_id} onValueChange={(v)=>setForm({...form, convention_id: v})}>
                      <SelectTrigger><SelectValue placeholder="Optionnel" /></SelectTrigger>
                      <SelectContent>{conventions.map(c=><SelectItem key={c.id} value={c.id}>{c.titre}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Date début (programmable) *</Label><Input type="datetime-local" required value={form.date_debut} onChange={(e)=>setForm({...form, date_debut: e.target.value})} data-testid="mis-date-debut" /></div>
                  <div><Label>Date fin *</Label><Input type="datetime-local" required value={form.date_fin} onChange={(e)=>setForm({...form, date_fin: e.target.value})} data-testid="mis-date-fin" /></div>
                  <div className="col-span-full"><Label>Lieu</Label><Input value={form.lieu} onChange={(e)=>setForm({...form, lieu: e.target.value})} /></div>
                  <div className="col-span-full"><Label>Description</Label><Textarea rows={2} value={form.description} onChange={(e)=>setForm({...form, description: e.target.value})} /></div>
                  <DialogFooter className="col-span-full"><Button type="submit" data-testid="mis-submit" className="bg-[#C5A059] hover:bg-[#A88846] text-white">Enregistrer</Button></DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        ) : null}
      />

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#C5A059]" /></div>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 p-12 text-center">
          <Briefcase className="h-10 w-10 mx-auto text-slate-300" />
          <p className="mt-3 text-slate-500">{user?.role === "salarie" ? "Aucune mission assignée pour le moment." : "Aucune mission planifiée."}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {filtered.map(m => (
            <div key={m.id} className="kpi-card bg-white border border-slate-200 rounded-xl p-5" data-testid={`mission-card-${m.id}`}>
              <div className="flex items-start justify-between gap-2">
                <h3 className="text-base font-semibold text-slate-900 min-w-0 truncate" style={{ fontFamily: "Outfit, sans-serif" }}>{m.libelle}</h3>
                <span className={`text-xs px-2 py-1 rounded shrink-0 ${STATUS[m.statut]?.cls || ""}`}>{STATUS[m.statut]?.label || m.statut}</span>
              </div>
              <div className="mt-2"><MissionCountdown dateDebut={m.date_debut} dateFin={m.date_fin} statut={m.statut} /></div>
              <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                {user?.role !== "salarie" && <div><div className="text-slate-400 uppercase tracking-wider text-[10px]">Entreprise</div><div className="text-slate-700 truncate">{entLabel(m.entreprise_id)}</div></div>}
                {user?.role !== "salarie" && <div><div className="text-slate-400 uppercase tracking-wider text-[10px]">Salarié</div><div className="text-slate-700 truncate">{salLabel(m.salarie_id)}</div></div>}
                <div><div className="text-slate-400 uppercase tracking-wider text-[10px]">Période</div><div className="text-slate-700">{String(m.date_debut).slice(0,10)} → {String(m.date_fin).slice(0,10)}</div></div>
                <div><div className="text-slate-400 uppercase tracking-wider text-[10px]">Lieu</div><div className="text-slate-700 truncate">{m.lieu || "—"}</div></div>
              </div>
              {m.description && <p className="mt-3 text-sm text-slate-600 line-clamp-2">{m.description}</p>}
              {canManage && (
                <div className="mt-4 pt-3 border-t border-slate-100 flex gap-1 flex-wrap">
                  {m.statut === "planifiee" && <Button size="sm" variant="outline" onClick={() => patchStatus(m.id, "en_cours")} data-testid={`mis-start-${m.id}`}>Démarrer</Button>}
                  {m.statut === "en_cours" && <Button size="sm" variant="outline" onClick={() => patchStatus(m.id, "terminee")} data-testid={`mis-end-${m.id}`}>Terminer</Button>}
                  {m.statut !== "desactivee" && m.statut !== "archivee" && <Button size="sm" variant="outline" onClick={() => patchStatus(m.id, "desactivee")} className="text-amber-700" data-testid={`mis-disable-${m.id}`}><Power className="h-3 w-3 mr-1" />Désactiver</Button>}
                  {m.statut === "desactivee" && <Button size="sm" variant="outline" onClick={() => patchStatus(m.id, "planifiee")} data-testid={`mis-enable-${m.id}`}><Power className="h-3 w-3 mr-1" />Réactiver</Button>}
                  {m.statut !== "archivee" && <Button size="sm" variant="outline" onClick={() => archive(m.id)} className="text-slate-600" data-testid={`mis-archive-${m.id}`}><Archive className="h-3 w-3 mr-1" />Archiver</Button>}
                  {user?.role === "admin" && m.statut === "archivee" && <Button size="sm" variant="outline" onClick={() => hardDelete(m.id)} className="text-red-600 border-red-300" data-testid={`mis-hard-${m.id}`}><Trash2 className="h-3 w-3" /></Button>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
